import React, { useEffect, useState } from 'react';
import { Alert, Button, message } from 'antd';
import { useTranslation } from '../../shared/i18n';
import billingService from '../services/billingService';

// Dismissed for a week (per browser); storage may be unavailable.
const KEY = 'yc_autorenew_nudge_dismissed';
const WEEK = 7 * 86400 * 1000;
const dismissedRecently = () => {
    try {
        return Date.now() - Number(localStorage.getItem(KEY) || 0) < WEEK;
    } catch (e) {
        return false;
    }
};

/**
 * Dismissible banner: subscriptions with auto-renew off that expire within 30 days, one-click enable.
 * Renders nothing when there are none. onEnabled is called after turning them on.
 */
const AutoRenewNudge = ({ onEnabled, style }) => {
    const { t } = useTranslation();
    const [items, setItems] = useState([]);
    const [hidden, setHidden] = useState(dismissedRecently());
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (hidden) return undefined;
        let alive = true;
        billingService.getAutoRenewSummary()
            .then((r) => { if (alive && r?.success) setItems(Array.isArray(r.data?.items) ? r.data.items : []); })
            .catch(() => {});
        return () => { alive = false; };
    }, [hidden]);

    if (hidden || items.length === 0) return null;

    const dismiss = () => {
        try { localStorage.setItem(KEY, String(Date.now())); } catch (e) { /* ignore */ }
        setHidden(true);
    };
    const enable = async () => {
        setBusy(true);
        try {
            const r = await billingService.enableAutoRenewAll(items.map((i) => i.id));
            if (r?.success) {
                message.success(r.message || t('Auto-renewal enabled'));
                setItems([]);
                onEnabled && onEnabled();
            } else {
                message.error(r?.error || t('Operation failed'));
            }
        } catch (e) {
            message.error(e?.error || e?.message || t('Operation failed'));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Alert
            type="warning"
            showIcon
            closable
            onClose={dismiss}
            style={{ marginBottom: 16, ...style }}
            message={t('{{count}} subscriptions expire soon with auto-renewal off', { count: items.length })}
            action={<Button size="small" type="primary" loading={busy} onClick={enable}>{t('Turn on auto-renewal')}</Button>}
        />
    );
};

export default AutoRenewNudge;
